/**
 * DiagnosticsTab — read-only view of the live preview dev-server health,
 * shown as the "Diagnostic" tab in the chat panel. Renders the
 * `FoundryPreviewDevState` the orchestrator publishes (status, session,
 * ports, URL diagnostics + warnings). Replaces the inline diagnostics
 * panel that used to live in the preview surface.
 */
import type { FoundryPreviewDevState } from '@volley/crucible-build-state/state-kernel.js';
import { cn } from '../lib/cn';

function statusLabel(status: FoundryPreviewDevState['status']): string {
  switch (status) {
    case 'running':
      return 'Running';
    case 'stale':
      return 'Needs restart';
    case 'failed':
      return 'Failed';
    case 'stopped':
      return 'Stopped';
    default:
      return 'Unknown';
  }
}

function statusColour(status: FoundryPreviewDevState['status']): string {
  if (status === 'running') return '#8fcf7a';
  if (status === 'stale' || status === 'failed') return '#f1b15f';
  return 'rgba(255,255,255,0.55)';
}

function Row({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2">
      <span className="text-white/35">{label}</span>
      <span className="truncate font-mono text-white/65" title={value}>
        {value}
      </span>
    </div>
  );
}

export function DiagnosticsTab({
  diagnostics,
}: {
  diagnostics: FoundryPreviewDevState | null | undefined;
}): React.JSX.Element {
  if (!diagnostics) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-white/35">
        No preview diagnostics yet — start the preview to see the dev-server status, ports, and
        connection details here.
      </div>
    );
  }

  const d = diagnostics;
  const ud = d.urlDiagnostics;
  const ports = d.ports;
  const warnings = ud?.warnings ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto p-4 text-[12px]">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/35">
          Preview diagnostics
        </span>
        <span className="font-semibold" style={{ color: statusColour(d.status) }} title={d.status}>
          {statusLabel(d.status)}
        </span>
      </div>

      <div className="grid gap-1 rounded-lg border border-white/10 bg-white/[0.03] p-3">
        <Row label="Reason" value={d.reason} />
        <Row label="Mode" value={d.previewMode} />
        <Row label="Session" value={d.sessionId ?? 'none'} />
        {ud?.displayUrl && <Row label="Display" value={ud.displayUrl} />}
        {ud?.controllerUrls?.[0] && <Row label="Controller" value={ud.controllerUrls[0]} />}
        {ud?.serverUrl && <Row label="Server" value={ud.serverUrl} />}
        {ud?.publicTunnelOrigin && <Row label="Tunnel" value={ud.publicTunnelOrigin} />}
        {ports && (
          <Row
            label="Ports"
            value={`display ${ports.display} · controller ${ports.controller} · server ${ports.server}`}
          />
        )}
      </div>

      {warnings.length > 0 && (
        <div className="grid gap-1.5" data-testid="diagnostics-tab-warnings">
          {warnings.map((w, i) => (
            <div
              key={`${w.code}-${w.target ?? i}`}
              className={cn(
                'rounded-md border border-amber-300/20 bg-amber-300/10 px-2.5 py-1.5',
                'text-[11px] leading-snug text-amber-100/85',
              )}
              title={w.url}
            >
              {w.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
